import { z } from "zod";
import { TimeSeriesPointSchema } from "./common";

export const TrafficSchema = z.object({
  totals: z.object({
    visitors: z.number(),
    uniqueVisitors: z.number(),
    returningVisitors: z.number(),
    peakBucket: z
      .object({
        bucket: z.string(),
        value: z.number(),
      })
      .nullable(),
  }),
  series: z.object({
    visitors: z.array(TimeSeriesPointSchema),
    uniqueVisitors: z.array(TimeSeriesPointSchema),
    returningVisitors: z.array(TimeSeriesPointSchema),
  }),
  /** Backend tags which upstream produced these numbers. */
  source: z.enum(["ga4", "log-analytics"]).optional(),
});
export type Traffic = z.infer<typeof TrafficSchema>;

export const HomepagePathEntrySchema = z.object({
  path: z.string(),
  entries: z.number(),
});
export type HomepagePathEntry = z.infer<typeof HomepagePathEntrySchema>;

export const HomepagePathsSchema = z.array(HomepagePathEntrySchema);
export type HomepagePaths = z.infer<typeof HomepagePathsSchema>;

export const JourneyStageSchema = z.object({
  name: z.string(),
  label: z.string(),
  order: z.number(),
  sessions: z.number(),
  dropoffFromPrevPct: z.number().nullable(),
});
export type JourneyStage = z.infer<typeof JourneyStageSchema>;

export const JourneySchema = z.object({
  stages: z.array(JourneyStageSchema),
});
export type Journey = z.infer<typeof JourneySchema>;

export const FunnelSchema = z.object({
  starts: z.number(),
  stepCompletions: z.number(),
  submits: z.number(),
  abandons: z.number(),
  abandonReasons: z.record(z.number()),
});
export type Funnel = z.infer<typeof FunnelSchema>;

export const ReservationUsersSchema = z.object({
  totalReservations: z.number(),
  identifiedReservations: z.number(),
  anonymousReservations: z.number(),
  distinctUsers: z.number(),
  avgReservationsPerUser: z.number(),
  repeatReserverRate: z.number(),
  distribution: z.array(
    z.object({ reservations: z.number(), users: z.number(), label: z.string() }),
  ),
});
export type ReservationUsers = z.infer<typeof ReservationUsersSchema>;

export const ConversionSchema = z.object({
  visitors: z.number(),
  starts: z.number(),
  submits: z.number(),
  startRatePct: z.number(),
  completionRatePct: z.number(),
  overallConversionPct: z.number(),
});
export type Conversion = z.infer<typeof ConversionSchema>;

export const AuthMetricsSchema = z.object({
  attempts: z.number(),
  succeeded: z.number(),
  failed: z.number(),
  successRatePct: z.number(),
  failureRatePct: z.number(),
  failuresByErrorCode: z.record(z.number()),
});
export type AuthMetrics = z.infer<typeof AuthMetricsSchema>;

export const PostLoginFboSchema = z.object({
  authedSearches: z.number(),
  anonSearches: z.number(),
  postLoginRatePct: z.number(),
});
export type PostLoginFbo = z.infer<typeof PostLoginFboSchema>;

export const WebVitalMetricSchema = z.enum([
  "web_vitals_lcp",
  "web_vitals_inp",
  "web_vitals_cls",
  "web_vitals_ttfb",
]);
export type WebVitalMetric = z.infer<typeof WebVitalMetricSchema>;

export const WebVitalSchema = z.object({
  metric: WebVitalMetricSchema,
  p75: z.array(TimeSeriesPointSchema),
});
export type WebVital = z.infer<typeof WebVitalSchema>;

export const PerformanceSchema = z.object({
  webVitals: z.array(WebVitalSchema),
  bff: z.object({
    latency: z.object({
      p50: z.array(TimeSeriesPointSchema),
      p95: z.array(TimeSeriesPointSchema),
      p99: z.array(TimeSeriesPointSchema),
    }),
    errorRate: z.array(TimeSeriesPointSchema),
  }),
});
export type Performance = z.infer<typeof PerformanceSchema>;
